import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Trash2, Save, ShieldCheck, CheckCheck, AlertTriangle, Info, XCircle } from "lucide-react";
import { Button, Skeleton } from "@finora/ui";
import type { CalcDto, CalcInputPayload, CalcPreviewDto } from "@finora/api-client";
import { api } from "../../lib/api";
import { DateInputDDMMYYYY } from "../../components/DateInputDDMMYYYY";
import { ProductAutocomplete } from "../../components/ProductAutocomplete";
import { InvoicePicker, type InvoiceDraft } from "./InvoicePicker";
import { fmt, STATUS_LABEL, STATUS_STYLE, TYPE_LABEL } from "./shared";

interface ELine {
  productId: string | null;
  sku: string;
  barcode: string;
  supplierCode: string;
  name: string;
  unit: string;
  quantity: string;
  purchasePrice: string;
  discountPercent: string;
  taxRatePercent: string;
  marginPercent: string;
  sellingPriceGross: string;
  priceManual: boolean;
  currentSellingGross: number | null;
  selected: boolean;
}
interface ECost {
  name: string;
  amount: string;
  allocationMethod: string;
}
interface EForm {
  type: string;
  documentDate: string;
  receiptDate: string;
  warehouseId: string;
  supplierId: string;
  supplierName: string;
  sourceDocNumber: string;
  sefInvoiceId: string;
  dueDate: string;
  currency: string;
  exchangeRate: string;
  note: string;
  lines: ELine[];
  costs: ECost[];
}

const inputCls = "h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none disabled:bg-slate-50";
const today = () => new Date().toISOString().slice(0, 10);
const n = (s: string) => {
  const v = Number(String(s).replace(",", "."));
  return Number.isFinite(v) ? v : 0;
};
const nOrNull = (s: string) => (s.trim() === "" ? null : n(s));
const str = (v: number | null | undefined) => (v === null || v === undefined ? "" : String(v));

const emptyLine = (): ELine => ({
  productId: null, sku: "", barcode: "", supplierCode: "", name: "", unit: "kom", quantity: "1", purchasePrice: "",
  discountPercent: "0", taxRatePercent: "20", marginPercent: "", sellingPriceGross: "", priceManual: false,
  currentSellingGross: null, selected: false,
});

function emptyForm(type: string): EForm {
  return {
    type, documentDate: today(), receiptDate: today(), warehouseId: "", supplierId: "", supplierName: "", sourceDocNumber: "",
    sefInvoiceId: "", dueDate: "", currency: "RSD", exchangeRate: "1", note: "", lines: [], costs: [],
  };
}

function fromDto(c: CalcDto): EForm {
  return {
    type: c.type,
    documentDate: c.documentDate.slice(0, 10),
    receiptDate: (c.receiptDate ?? "").slice(0, 10),
    warehouseId: c.warehouseId ?? "",
    supplierId: c.supplierId ?? "",
    supplierName: c.supplierName ?? "",
    sourceDocNumber: c.sourceDocNumber ?? "",
    sefInvoiceId: c.sefInvoiceId ?? "",
    dueDate: (c.dueDate ?? "").slice(0, 10),
    currency: c.currency ?? "RSD",
    exchangeRate: str(c.exchangeRate ?? 1),
    note: c.note ?? "",
    lines: c.lines.map((l) => ({
      productId: l.productId, sku: l.sku ?? "", barcode: l.barcode ?? "", supplierCode: l.supplierCode ?? "", name: l.name,
      unit: l.unit, quantity: str(l.quantity), purchasePrice: str(l.purchasePrice), discountPercent: str(l.discountPercent),
      taxRatePercent: str(l.taxRatePercent), marginPercent: str(l.marginPercent), sellingPriceGross: str(l.sellingPriceGross),
      priceManual: l.priceManual, currentSellingGross: l.currentSellingGross ?? null, selected: false,
    })),
    costs: c.costs.map((x) => ({ name: x.name, amount: str(x.amount), allocationMethod: x.allocationMethod })),
  };
}

function toPayload(f: EForm, pricing?: CalcInputPayload["pricing"]): CalcInputPayload {
  return {
    type: f.type,
    documentDate: f.documentDate || null,
    receiptDate: f.receiptDate || null,
    warehouseId: f.warehouseId || null,
    supplierId: f.supplierId || null,
    sourceDocNumber: f.sourceDocNumber || null,
    sefInvoiceId: f.sefInvoiceId || null,
    dueDate: f.dueDate || null,
    currency: f.currency || "RSD",
    exchangeRate: n(f.exchangeRate) || 1,
    note: f.note || null,
    pricing: pricing ?? null,
    lines: f.lines.map((l) => ({
      productId: l.productId, sku: l.sku || null, barcode: l.barcode || null, supplierCode: l.supplierCode || null,
      name: l.name, unit: l.unit || "kom", quantity: n(l.quantity), purchasePrice: n(l.purchasePrice),
      discountPercent: n(l.discountPercent), taxRatePercent: n(l.taxRatePercent || "20"),
      marginPercent: nOrNull(l.marginPercent), sellingPriceGross: nOrNull(l.sellingPriceGross), priceManual: l.priceManual,
    })),
    costs: f.costs.map((c) => ({ name: c.name, amount: n(c.amount), allocationMethod: c.allocationMethod })),
  };
}

const TABS = [
  ["osnovni", "Osnovni podaci"],
  ["stavke", "Stavke"],
  ["troskovi", "Zavisni troškovi"],
  ["cene", "Prodajne cene"],
  ["pregled", "Pregled i kontrola"],
  ["istorija", "Istorija"],
] as const;
type TabKey = (typeof TABS)[number][0];

export function CalculationEditor({ target, onClose, onOpen }: { target: string; onClose: () => void; onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const isNew = target.startsWith("new:");
  const [id, setId] = useState<string | null>(isNew ? null : target);
  const [form, setForm] = useState<EForm>(() => emptyForm(isNew ? target.slice(4) : "RECEIPT"));
  const [loaded, setLoaded] = useState(isNew);
  const [tab, setTab] = useState<TabKey>("osnovni");
  const [preview, setPreview] = useState<CalcPreviewDto | null>(null);
  const [saved, setSaved] = useState<CalcDto | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [pricing, setPricing] = useState({ mode: "MARKUP" as "MARKUP" | "MARGIN", marginPercent: "20", rounding: "NONE" as "NONE" | "WHOLE" | "TEN" | "NINETY_NINE" });
  const dirty = useRef(false);

  const status = saved?.status ?? "DRAFT";
  const readOnly = !["DRAFT", "CALCULATED", "PENDING_APPROVAL"].includes(status);
  const isStock = form.type !== "PRICE_CHANGE";

  const warehouses = useQuery({ queryKey: ["warehouses"], queryFn: () => api.listWarehouses() });
  const suppliers = useQuery({ queryKey: ["suppliers"], queryFn: () => api.listSuppliers() });

  const existing = useQuery({ queryKey: ["calculation", target], queryFn: () => api.getCalculation(target), enabled: !isNew });
  useEffect(() => {
    if (existing.data && !loaded) {
      setForm(fromDto(existing.data));
      setSaved(existing.data);
      setLoaded(true);
    }
  }, [existing.data, loaded]);

  // podrazumevani magacin za novu kalkulaciju
  useEffect(() => {
    if (isNew && !form.warehouseId && warehouses.data?.length) {
      const def = warehouses.data.find((w) => w.isDefault) ?? warehouses.data[0]!;
      setForm((f) => ({ ...f, warehouseId: def.id }));
    }
  }, [isNew, form.warehouseId, warehouses.data]);

  // obračun uživo (desni panel)
  const payloadKey = useMemo(() => JSON.stringify(toPayload(form)), [form]);
  useEffect(() => {
    if (!loaded) return;
    let cancelled = false;
    const t = setTimeout(() => {
      api.previewCalculation(JSON.parse(payloadKey) as CalcInputPayload).then((p) => !cancelled && setPreview(p)).catch(() => undefined);
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [payloadKey, loaded]);

  const set = <K extends keyof EForm>(k: K, v: EForm[K]) => {
    dirty.current = true;
    setForm((f) => ({ ...f, [k]: v }));
  };
  // Skener: kad se upiše bar kod (pauza 1 s ili Enter), nađi artikal u šifarniku i upiši šifru/naziv,
  // da se artikli ne dupliraju.
  const barcodeTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const lookupBarcode = async (i: number, code: string) => {
    const c = code.trim();
    if (c.length < 6) return;
    try {
      const found = (await api.listProducts({ search: c })) as any[];
      const p = found.find((x) => (x.barcode ?? "").trim() === c);
      if (!p) return;
      setLine(i, {
        productId: p.id,
        sku: p.sku,
        barcode: p.barcode ?? c,
        name: p.name,
        unit: p.unit,
        taxRatePercent: String(p.taxRate?.ratePercent ?? 20),
        currentSellingGross: Number(p.unitPriceGross),
        ...(p.purchasePrice ? { purchasePrice: String(p.purchasePrice) } : {}),
      } as Partial<ELine>);
      setTimeout(() => document.querySelector<HTMLInputElement>(`[data-qty="${i}"]`)?.focus(), 0);
    } catch {
      /* tiho - ostaje ručni unos */
    }
  };
  const onBarcodeChange = (i: number, value: string) => {
    setLine(i, { barcode: value });
    clearTimeout(barcodeTimers.current[i]);
    barcodeTimers.current[i] = setTimeout(() => lookupBarcode(i, value), 1000);
  };

  const setLine = (i: number, patch: Partial<ELine>) => {
    dirty.current = true;
    setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));
  };

  function applyDraft(d: InvoiceDraft) {
    dirty.current = true;
    setForm((f) => ({
      ...f,
      sourceDocNumber: d.sourceDocNumber ?? f.sourceDocNumber,
      sefInvoiceId: d.sefInvoiceId ?? "",
      supplierId: d.supplier?.id ?? f.supplierId,
      supplierName: d.supplier?.name ?? f.supplierName,
      dueDate: d.dueDate ? d.dueDate.slice(0, 10) : f.dueDate,
      currency: d.currency ?? f.currency,
      lines: (d.lines ?? []).map((l) => ({
        ...emptyLine(), productId: l.productId, sku: l.sku ?? "", barcode: l.barcode ?? "", supplierCode: l.supplierCode ?? "",
        name: l.name, unit: l.unit || "kom", quantity: str(l.quantity), purchasePrice: str(l.purchasePrice),
        discountPercent: str(l.discountPercent), taxRatePercent: str(l.taxRatePercent),
      })),
    }));
    if (suppliers.data && d.supplier && !suppliers.data.some((s) => s.id === d.supplier!.id)) void suppliers.refetch();
    setTab("stavke");
  }

  const save = useMutation({
    mutationFn: async () => (id ? api.updateCalculation(id, toPayload(form)) : api.createCalculation(toPayload(form))),
    onSuccess: (c) => {
      dirty.current = false;
      setSaved(c);
      setMsg({ ok: true, text: "Sačuvano." });
      void qc.invalidateQueries({ queryKey: ["calculations"] });
      if (!id) {
        setId(c.id);
        onOpen(c.id);
      }
    },
    onError: (e: Error) => setMsg({ ok: false, text: e.message }),
  });

  async function act(action: "check" | "submit" | "approve" | "post") {
    setMsg(null);
    try {
      let cid = id;
      if (!readOnly) {
        const c = cid ? await api.updateCalculation(cid, toPayload(form)) : await api.createCalculation(toPayload(form));
        cid = c.id;
        setId(c.id);
      }
      const r = await api.calculationAction(cid!, action);
      dirty.current = false;
      setSaved(r);
      setForm(fromDto(r));
      void qc.invalidateQueries({ queryKey: ["calculations"] });
      const text =
        action === "post" ? "Kalkulacija je proknjižena – roba je na lageru, cene su ažurirane."
        : action === "check" ? (r.warnings.some((w) => w.level === "ERROR") ? "Provera: postoje greške." : "Provera uspešna.")
        : "Gotovo.";
      setMsg({ ok: true, text });
      if (action === "check") setTab("pregled");
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : "Greška." });
    }
  }

  async function applyPricing(onlySelected: boolean) {
    const idx = form.lines.map((l, i) => (l.selected ? i : -1)).filter((i) => i >= 0);
    if (onlySelected && idx.length === 0) {
      setMsg({ ok: false, text: "Označite bar jednu stavku." });
      return;
    }
    const p = await api.previewCalculation(
      toPayload(form, { mode: pricing.mode, marginPercent: n(pricing.marginPercent), rounding: pricing.rounding, lineIndexes: onlySelected ? idx : undefined }),
    );
    dirty.current = true;
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, i) => {
        const r = p.lines[i];
        if (!r || (onlySelected && !idx.includes(i))) return l;
        return { ...l, marginPercent: str(r.marginPercent), sellingPriceGross: str(r.sellingPriceGross), priceManual: true };
      }),
    }));
    setMsg({ ok: true, text: "Cene primenjene." });
  }

  if (!loaded) return <Skeleton className="h-60 w-full" />;

  const s = preview?.summary ?? saved?.summary;
  const warnings = preview?.warnings ?? saved?.warnings ?? [];
  const errCount = warnings.filter((w) => w.level === "ERROR").length;
  const warnCount = warnings.filter((w) => w.level === "WARNING").length;
  const lineCalc = (i: number) => preview?.lines[i]?.calc;

  function prefillSellingPrices() {
    if (readOnly) return;
    const need = form.lines.some((l) => !l.priceManual && l.marginPercent.trim() === "" && l.sellingPriceGross.trim() === "" && l.purchasePrice.trim() !== "");
    if (!need) return;
    dirty.current = true;
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l) =>
        !l.priceManual && l.marginPercent.trim() === "" && l.sellingPriceGross.trim() === "" && l.purchasePrice.trim() !== ""
          ? { ...l, sellingPriceGross: l.purchasePrice, priceManual: true }
          : l,
      ),
    }));
  }
  const grossPurchase = form.lines.reduce((a, l) => a + n(l.quantity) * n(l.purchasePrice), 0);
  const netPurchase = form.lines.reduce((a, l) => a + n(l.quantity) * n(l.purchasePrice) * (1 - n(l.discountPercent) / 100), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" title="Nazad na listu">
            <ArrowLeft className="h-5 w-5" aria-hidden />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-900">{saved?.number ?? "Nova kalkulacija"}</h2>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[status] ?? ""}`}>{STATUS_LABEL[status] ?? status}</span>
            </div>
            <div className="text-sm text-slate-500">{TYPE_LABEL[form.type] ?? form.type}</div>
          </div>
        </div>
        {!readOnly && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => save.mutate()} disabled={save.isPending}>
              <Save className="mr-1 h-4 w-4" aria-hidden /> Sačuvaj
            </Button>
            {id && (
              <Button variant="secondary" onClick={() => void act("check")}>
                <ShieldCheck className="mr-1 h-4 w-4" aria-hidden /> Provera
              </Button>
            )}
            {id && status !== "PENDING_APPROVAL" && (
              <Button variant="secondary" onClick={() => void act("submit")}>Pošalji na odobravanje</Button>
            )}
            {id && status === "PENDING_APPROVAL" && (
              <Button variant="secondary" onClick={() => void act("approve")}>Odobri</Button>
            )}
            <Button
              onClick={() => window.confirm(isStock ? "Proknjižiti kalkulaciju? Roba se evidentira na lageru i menjaju se cene artikala." : "Primeniti nove cene na artikle?") && void act("post")}
            >
              <CheckCheck className="mr-1 h-4 w-4" aria-hidden /> Potvrdi i knjiži
            </Button>
          </div>
        )}
      </div>

      {msg && (
        <div className={`rounded-lg border px-3 py-2 text-sm ${msg.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-700"}`}>{msg.text}</div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_300px]">
        <div className="min-w-0 space-y-4">
          <div className="flex gap-1 overflow-x-auto border-b border-border">
            {TABS.map(([k, label]) => (
              <button
                key={k}
                onClick={() => { if (k === "cene") prefillSellingPrices(); setTab(k); }}
                className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${tab === k ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              >
                {label}
                {k === "pregled" && errCount + warnCount > 0 && (
                  <span className={`ml-1 rounded-full px-1.5 text-[10px] ${errCount ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{errCount + warnCount}</span>
                )}
              </button>
            ))}
          </div>

          {tab === "osnovni" && (
            <div className="space-y-4 rounded-xl border border-border bg-surface p-4">
              {isStock && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Broj fakture (iz SEF-a)</label>
                  <InvoicePicker value={form.sourceDocNumber} disabled={readOnly} onPick={applyDraft} />
                  {form.supplierName && (
                    <div className="mt-1 text-xs text-slate-500">
                      Firma koja je izdala fakturu: <span className="font-medium text-slate-700">{form.supplierName}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                {isStock && (
                  <>
                    <Field label="Dobavljač *">
                      <select disabled={readOnly} className={inputCls} value={form.supplierId} onChange={(e) => set("supplierId", e.target.value)}>
                        <option value="">— izaberite —</option>
                        {suppliers.data?.map((sp) => (
                          <option key={sp.id} value={sp.id}>{sp.name}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Magacin *">
                      <select disabled={readOnly} className={inputCls} value={form.warehouseId} onChange={(e) => set("warehouseId", e.target.value)}>
                        <option value="">— izaberite —</option>
                        {warehouses.data?.map((w) => (
                          <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Datum prijema">
                      <DateInputDDMMYYYY value={form.receiptDate} onChange={(v) => set("receiptDate", v)} />
                    </Field>
                    <Field label="Rok plaćanja">
                      <DateInputDDMMYYYY value={form.dueDate} onChange={(v) => set("dueDate", v)} />
                    </Field>
                    <Field label="Valuta">
                      <input disabled={readOnly} className={inputCls} value={form.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} />
                    </Field>
                    {form.currency !== "RSD" && (
                      <Field label="Kurs">
                        <input disabled={readOnly} className={inputCls} value={form.exchangeRate} onChange={(e) => set("exchangeRate", e.target.value)} />
                      </Field>
                    )}
                  </>
                )}
                <Field label="Datum kalkulacije">
                  <DateInputDDMMYYYY value={form.documentDate} onChange={(v) => set("documentDate", v)} />
                </Field>
              </div>
              <Field label="Napomena">
                <textarea disabled={readOnly} className="min-h-[70px] w-full rounded-lg border border-border px-2 py-1.5 text-sm focus:border-brand focus:outline-none" value={form.note} onChange={(e) => set("note", e.target.value)} />
              </Field>
            </div>
          )}

          {tab === "stavke" && (
            <div className="space-y-3 rounded-xl border border-border bg-surface p-4">
              {!readOnly && (
                <div className="flex flex-wrap items-center gap-2">
                  <div className="w-72">
                    <ProductAutocomplete
                      placeholder="Dodaj artikal iz šifarnika…"
                      onSelect={(p) => {
                        dirty.current = true;
                        setForm((f) => ({
                          ...f,
                          lines: [
                            ...f.lines,
                            {
                              ...emptyLine(), productId: p.id, sku: p.sku, barcode: p.barcode ?? "", name: p.name, unit: p.unit,
                              purchasePrice: p.purchasePrice ?? "", taxRatePercent: String(p.taxRate?.ratePercent ?? 20),
                              currentSellingGross: Number(p.unitPriceGross),
                            },
                          ],
                        }));
                      }}
                    />
                  </div>
                  {isStock && (
                    <button className="flex h-9 items-center gap-1 rounded-lg border border-border px-3 text-sm hover:bg-slate-50" onClick={() => set("lines", [...form.lines, emptyLine()])}>
                      <Plus className="h-4 w-4" aria-hidden /> Prazna stavka
                    </button>
                  )}
                </div>
              )}
              {form.lines.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-500">
                  Nema stavki. Izaberite fakturu iz SEF-a na kartici „Osnovni podaci” ili dodajte artikle ručno.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-1 py-2">#</th>
                        <th className="px-1">Bar kod</th>
                        <th className="px-1">Šifra</th>
                        <th className="px-1">Naziv</th>
                        <th className="px-1">JM</th>
                        <th className="px-1 text-right">Količina</th>
                        <th className="px-1 text-right">Nabavna cena</th>
                        <th className="px-1 text-right">Rabat %</th>
                        <th className="px-1 text-right">PDV %</th>
                        <th className="px-1 text-right">Vrednost</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {form.lines.map((l, i) => (
                        <tr key={i} className="border-t border-border align-top">
                          <td className="px-1 py-1.5 text-slate-400">{i + 1}</td>
                          <td className="w-44 px-1 py-1"><input disabled={readOnly} className={inputCls} value={l.barcode} onChange={(e) => onBarcodeChange(i, e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); clearTimeout(barcodeTimers.current[i]); lookupBarcode(i, l.barcode); } }} /></td>
                          <td className="w-28 px-1 py-1"><input disabled={readOnly} className={inputCls} value={l.sku} onChange={(e) => setLine(i, { sku: e.target.value })} /></td>
                          <td className="min-w-[220px] px-1 py-1">
                            <input disabled={readOnly} className={inputCls} value={l.name} onChange={(e) => setLine(i, { name: e.target.value, productId: l.productId })} />
                            {!l.productId && <span className="text-[10px] text-amber-600">novi artikal</span>}
                          </td>
                          <td className="w-14 px-1 py-1"><input disabled={readOnly} className={inputCls} value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} /></td>
                          <td className="w-16 px-1 py-1"><input data-qty={i} disabled={readOnly} className={`${inputCls} text-right`} value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></td>
                          <td className="w-24 px-1 py-1"><input disabled={readOnly} className={`${inputCls} text-right`} value={l.purchasePrice} onChange={(e) => setLine(i, { purchasePrice: e.target.value })} /></td>
                          <td className="w-14 px-1 py-1"><input disabled={readOnly} className={`${inputCls} text-right`} value={l.discountPercent} onChange={(e) => setLine(i, { discountPercent: e.target.value })} /></td>
                          <td className="w-14 px-1 py-1"><input disabled={readOnly} className={`${inputCls} text-right`} value={l.taxRatePercent} onChange={(e) => setLine(i, { taxRatePercent: e.target.value })} /></td>
                          <td className="w-28 px-1 py-2 text-right font-medium">{fmt(lineCalc(i)?.lineNet)}</td>
                          <td className="px-1 py-1">
                            {!readOnly && (
                              <button className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" onClick={() => set("lines", form.lines.filter((_, idx) => idx !== i))}>
                                <Trash2 className="h-4 w-4" aria-hidden />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "troskovi" && (
            <div className="space-y-3 rounded-xl border border-border bg-surface p-4">
              <p className="text-sm text-slate-500">Zavisni troškovi nabavke (transport, špedicija, carina…) raspoređuju se na stavke i uvećavaju nabavnu vrednost.</p>
              {form.costs.map((c, i) => (
                <div key={i} className="grid grid-cols-[1fr_130px_160px_36px] gap-2">
                  <input disabled={readOnly} className={inputCls} placeholder="Naziv troška" value={c.name} onChange={(e) => set("costs", form.costs.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)))} />
                  <input disabled={readOnly} className={`${inputCls} text-right`} placeholder="Iznos" value={c.amount} onChange={(e) => set("costs", form.costs.map((x, k) => (k === i ? { ...x, amount: e.target.value } : x)))} />
                  <select disabled={readOnly} className={inputCls} value={c.allocationMethod} onChange={(e) => set("costs", form.costs.map((x, k) => (k === i ? { ...x, allocationMethod: e.target.value } : x)))}>
                    <option value="BY_VALUE">Po vrednosti</option>
                    <option value="BY_QUANTITY">Po količini</option>
                  </select>
                  {!readOnly && (
                    <button className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" onClick={() => set("costs", form.costs.filter((_, k) => k !== i))}>
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  )}
                </div>
              ))}
              {!readOnly && (
                <button className="flex h-9 items-center gap-1 rounded-lg border border-border px-3 text-sm hover:bg-slate-50" onClick={() => set("costs", [...form.costs, { name: "", amount: "", allocationMethod: "BY_VALUE" }])}>
                  <Plus className="h-4 w-4" aria-hidden /> Dodaj trošak
                </button>
              )}
              <div className="text-right text-sm font-medium">Ukupno troškovi: {fmt(s?.totalCosts)}</div>
            </div>
          )}

          {tab === "cene" && (
            <div className="space-y-3 rounded-xl border border-border bg-surface p-4">
              {!readOnly && (
                <div className="flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 p-3">
                  <Field label="Način">
                    <select className={inputCls} value={pricing.mode} onChange={(e) => setPricing({ ...pricing, mode: e.target.value as "MARKUP" | "MARGIN" })}>
                      <option value="MARKUP">Marža na nabavnu cenu</option>
                      <option value="MARGIN">Marža u prodajnoj ceni</option>
                    </select>
                  </Field>
                  <div className="w-24">
                    <Field label="Marža %">
                      <input className={`${inputCls} text-right`} value={pricing.marginPercent} onChange={(e) => setPricing({ ...pricing, marginPercent: e.target.value })} />
                    </Field>
                  </div>
                  <Field label="Zaokruživanje">
                    <select className={inputCls} value={pricing.rounding} onChange={(e) => setPricing({ ...pricing, rounding: e.target.value as typeof pricing.rounding })}>
                      <option value="NONE">Bez zaokruživanja</option>
                      <option value="WHOLE">Na ceo broj</option>
                      <option value="TEN">Na 10 dinara</option>
                      <option value="NINETY_NINE">Na .99</option>
                    </select>
                  </Field>
                  <Button onClick={() => void applyPricing(false)}>Primeni na sve</Button>
                  <Button variant="secondary" onClick={() => void applyPricing(true)}>Primeni na označene</Button>
                </div>
              )}
              {form.lines.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-500">Nema stavki.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-1 py-2">
                          <input type="checkbox" onChange={(e) => set("lines", form.lines.map((l) => ({ ...l, selected: e.target.checked })))} />
                        </th>
                        <th className="px-1">Artikal</th>
                        <th className="px-1 text-right">Nabavna vr./jed.</th>
                        <th className="px-1 text-right">Trenutna prodajna</th>
                        <th className="px-1 text-right">Marža %</th>
                        <th className="px-1 text-right">Nova prodajna (sa PDV)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.lines.map((l, i) => {
                        const c = lineCalc(i);
                        return (
                          <tr key={i} className="border-t border-border">
                            <td className="px-1 py-1.5"><input type="checkbox" checked={l.selected} onChange={(e) => setLine(i, { selected: e.target.checked })} /></td>
                            <td className="px-1 py-1.5">{l.name}</td>
                            <td className="px-1 py-1.5 text-right">{fmt(c?.unitCost)}</td>
                            <td className="px-1 py-1.5 text-right text-slate-500">{fmt(l.currentSellingGross)}</td>
                            <td className="w-24 px-1 py-1">
                              <input
                                disabled={readOnly}
                                className={`${inputCls} text-right`}
                                value={l.priceManual ? (c?.marginPercent !== null && c?.marginPercent !== undefined ? String(c.marginPercent) : "") : l.marginPercent}
                                onChange={(e) => setLine(i, { marginPercent: e.target.value, sellingPriceGross: "", priceManual: false })}
                              />
                            </td>
                            <td className="w-32 px-1 py-1">
                              <input
                                disabled={readOnly}
                                className={`${inputCls} text-right font-medium`}
                                value={l.priceManual ? l.sellingPriceGross : c?.sellingPriceGross !== null && c?.sellingPriceGross !== undefined ? String(c.sellingPriceGross) : ""}
                                onChange={(e) => setLine(i, { sellingPriceGross: e.target.value, priceManual: e.target.value.trim() !== "" })}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "pregled" && (
            <div className="space-y-3 rounded-xl border border-border bg-surface p-4">
              {warnings.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-emerald-700"><ShieldCheck className="h-4 w-4" aria-hidden /> Nema upozorenja – kalkulacija je spremna za knjiženje.</div>
              ) : (
                warnings.map((w, i) => (
                  <div key={i} className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${w.level === "ERROR" ? "bg-red-50 text-red-700" : w.level === "WARNING" ? "bg-amber-50 text-amber-800" : "bg-slate-50 text-slate-600"}`}>
                    {w.level === "ERROR" ? <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /> : w.level === "WARNING" ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /> : <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />}
                    {w.message}
                  </div>
                ))
              )}
              {isStock && (!form.supplierId || !form.warehouseId) && (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">Za knjiženje je potrebno izabrati dobavljača i magacin.</div>
              )}
            </div>
          )}

          {tab === "istorija" && (
            <div className="rounded-xl border border-border bg-surface p-4">
              {!saved?.history.length ? (
                <div className="text-sm text-slate-500">Istorija će se pojaviti nakon čuvanja.</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {saved.history.map((h) => (
                    <li key={h.id} className="flex justify-between gap-3 border-b border-border pb-2 last:border-0">
                      <span><span className="font-medium">{h.action}</span>{h.details ? ` – ${h.details}` : ""}</span>
                      <span className="shrink-0 text-slate-500">{h.userName ?? "—"} · {new Date(h.createdAt).toLocaleString("sr-RS")}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <aside className="space-y-3">
          <div className="rounded-xl border border-border bg-surface p-4">
            <h3 className="mb-2 text-sm font-semibold text-slate-900">Obračun</h3>
            <dl className="space-y-1.5 text-sm">
              <Row k="Nabavna bez rabata (bez PDV)" v={fmt(grossPurchase)} />
              <Row k="Nabavna sa popustom (bez PDV)" v={fmt(netPurchase)} />
              <Row k="Razlika (ušteda)" v={fmt(grossPurchase - netPurchase)} bold />
              <Row k="Nabavna vrednost" v={fmt(s?.totalPurchase)} />
              <Row k="Zavisni troškovi" v={fmt(s?.totalCosts)} />
              <Row k="Vrednost sa troškovima" v={fmt(s?.totalValue)} bold />
              <Row k="PDV" v={fmt(s?.totalVat)} />
              <Row k="Ukupno (sa PDV)" v={fmt(s?.totalGross)} bold />
              <Row k="Prosečna marža" v={s?.avgMarkupPercent != null ? `${fmt(s.avgMarkupPercent)} %` : "—"} />
              <Row k="Prosečna prodajna cena" v={fmt(s?.avgSellingGross)} />
            </dl>
          </div>
          <div className="rounded-xl border border-border bg-surface p-4">
            <h3 className="mb-2 text-sm font-semibold text-slate-900">Informacije</h3>
            <dl className="space-y-1.5 text-sm">
              <Row k="Stavki" v={String(form.lines.length)} />
              <Row k="Ukupna količina" v={fmt(s?.totalQuantity, 0)} />
              <Row k="Dobavljač" v={form.supplierName || suppliers.data?.find((x) => x.id === form.supplierId)?.name || "—"} />
              <Row k="Faktura" v={form.sourceDocNumber || "—"} />
              <Row k="Greške" v={String(errCount)} />
              <Row k="Upozorenja" v={String(warnCount)} />
            </dl>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
      {children}
    </div>
  );
}

function Row({ k, v, bold }: { k: string; v: string; bold?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500">{k}</dt>
      <dd className={`text-right ${bold ? "font-semibold text-slate-900" : "text-slate-700"}`}>{v}</dd>
    </div>
  );
}
